'use client'

import type { DeliveryMethod } from '@repo/core/delivery'
import { ArrowRight } from 'lucide-react'
import Link from 'next/link'
import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'

import { CodeSlots } from '@/components/atoms/code-slots.client'
import { SlideCommit } from '@/components/atoms/slide-commit.client'
import { AuthCard, AuthEyebrow, AuthField } from '@/components/molecules/auth-card'
import { useToast } from '@/components/providers/ToastProvider'
import { ConsentCheckbox } from '@/components/sections/creator/consent-checkbox.client'
import { DeliveryChoice } from '@/components/sections/creator/delivery-choice'
import { NextStepsNote } from '@/components/sections/creator/next-steps-note'
import type { Placed } from '@/components/sections/creator/success-panel'
import { OrderSignInCard } from '@/components/sections/order/order-sign-in-card.client'
import type { CheckoutConflict } from '@/lib/checkout-conflict'
import { BODY, META } from '@/lib/typography'
import { cn } from '@/lib/utils'
import { checkoutAction } from '@/server/checkout'

/**
 * Contact details and the order slider — the only `checkoutAction` call site.
 *
 * It lives beside the order now rather than a page away, which is the whole
 * point of the merge: the reader configures, sees the total and orders without
 * a navigation in between.
 *
 * Nothing personal touches the basket. The basket is a row in Postgres holding
 * WHAT was chosen; these fields stay in component state and go straight to the
 * server action. There are deliberately no contact columns on `cart` — see its
 * docblock for why adding one is a decision about consent, not about schema.
 */
export function OrderContactCard({
  id,
  locale,
  signedIn,
  lineCount,
  coordinator,
  hasRental,
  delivery,
  setDelivery,
  needsMaterialConsent,
  terms,
  blocked,
  flushLines,
  onPlaced,
  className,
}: {
  /** Anchor for the phone bar, which jumps here rather than to the arithmetic. */
  id?: string
  locale: string
  /** An order is a commitment to pay up front, and paying needs an account. */
  signedIn: boolean
  /**
   * How many lines the basket HOLDS — finished or not. A COUNT, not the lines:
   * the server reads them from the basket row itself, so sending them would be
   * sending a list nobody uses.
   *
   * It used to be the finished count, which made "empty" and "unfinished" the
   * same state on this card: a basket of two half-filled positions offered
   * "Dodaj pozycję do zamówienia" and hid the sentence saying what was actually
   * missing. `blocked` is the one that refuses an unfinished order.
   */
  lineCount: number
  /** Spec §13's tick, decided beside the money in `OrderTotalCard`. */
  coordinator: boolean
  /** Does the basket hold equipment? Decides whether spec §11's two asks appear. */
  hasRental: boolean
  delivery: DeliveryMethod
  setDelivery: (next: DeliveryMethod) => void
  /**
   * Does this basket carry source material (spec §7 · G4)?
   *
   * Decided by the page from the same fact `createOrder` decides it from, so
   * the tick box appears exactly when the server will insist on it — a checkout
   * that refuses over a box it never showed is the failure this prop prevents.
   */
  needsMaterialConsent: boolean
  /**
   * The pre-order information block (u. o prawach konsumenta art. 12).
   * Rendered on the SERVER by the route and handed here as a slot — it reads
   * the seller's identity and whether a gateway is configured, and neither
   * belongs in this component's bundle.
   */
  terms: ReactNode
  /**
   * Close the basket's debounce and wait for it.
   *
   * A line is written a pause after the last keystroke, and the checkout is
   * judged on the ROW, not on the screen — so somebody who types the brief and
   * reaches straight for the button is refused for a line that is finished in
   * front of them. See `line-writes.ts`; this is the prop that shuts the window.
   */
  flushLines: () => Promise<void>
  /**
   * Why the order cannot be placed yet, in Polish, or `null`. Unfinished lines
   * now LIVE in the order instead of being held outside it by a disabled add
   * button, so the reason has to be said here rather than implied by a control
   * that never explains itself.
   */
  blocked: string | null
  onPlaced: (result: Placed) => void
  className?: string
}) {
  const [data, setData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    companyName: '',
    taxId: '',
  })
  const [accepted, setAccepted] = useState(false)
  const [materialOk, setMaterialOk] = useState(false)
  const [rentalOk, setRentalOk] = useState(false)
  /**
   * A code, as typed. Uppercased and stripped of anything that is not a letter,
   * a digit or a dash — the lookup is case-insensitive anyway, and the point is
   * that what somebody sees in the box matches what was printed on the card
   * they are copying from.
   *
   * The stripping is `CodeSlots`' own and used to be duplicated here. Its
   * `sanitize` is the same alphabet, dash included, and it applies the ceiling
   * AFTER stripping rather than before — which is the order that survives
   * pasting `Kod: WIOSNA24` out of an e-mail. One filter, in the field.
   */
  const [code, setCode] = useState('')
  /**
   * Invoice details, hidden until asked for.
   *
   * The order charges 23% VAT and the confirmation promises an invoice, but the
   * form collected no company name and no NIP — so every business order arrived
   * needing a follow-up email before it could be invoiced at all. `contact`
   * already carried both fields; nothing asked for them.
   *
   * Behind a toggle rather than always visible, because most orders are not
   * a company's and two empty fields on a checkout are two reasons to hesitate.
   */
  const [invoice, setInvoice] = useState(false)
  const [busy, setBusy] = useState(false)
  /**
   * How many checkout runs have finished here, successful or not.
   *
   * It is the slider's `key` and it has no other job. `SlideCommit`'s commit
   * latch never reopens on its own — that is what makes a double-fire
   * impossible rather than merely unlikely — so a NEW instance is the only way
   * back from a refused order. See the control at the bottom of this card.
   */
  const [attempt, setAttempt] = useState(0)
  /**
   * The wrapper the slider is remounted inside, and whether the reader was
   * standing on it when it went.
   *
   * A new `key` throws the old DOM node away, and focus goes with it — onto
   * `<body>`, from where the way back to the control is a dozen Tab presses
   * through a filled-in checkout. That is the whole keyboard path to paying,
   * broken by the one event that most needs it to work: an order that was
   * refused. The flag is read at the moment the remount is ORDERED rather than
   * afterwards, so a reader who wandered off to fix a date while the call was
   * in flight does not have the page snatched back from under them.
   */
  const commitRef = useRef<HTMLDivElement>(null)
  const restoreFocus = useRef(false)
  const { toast } = useToast()

  /** Hand the slider a fresh instance — see {@link attempt}. */
  const reopenSlider = () => {
    restoreFocus.current = Boolean(commitRef.current?.contains(document.activeElement))
    setAttempt((n) => n + 1)
  }

  useEffect(() => {
    // `attempt` is the trigger rather than a value: 0 is the first paint, where
    // there is nothing to restore and stealing focus would drag the page down
    // to the bottom of the form on arrival.
    if (attempt === 0 || !restoreFocus.current) return
    restoreFocus.current = false
    commitRef.current?.querySelector<HTMLElement>('[role="button"]')?.focus()
  }, [attempt])

  /** Nothing in the basket at all — different from nothing FINISHED, see `blocked`. */
  const cartEmpty = lineCount === 0
  /** Dated lines whose window went while the basket was sitting there. */
  const [conflicts, setConflicts] = useState<CheckoutConflict[]>([])

  const onChange = (key: keyof typeof data) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setData((d) => ({ ...d, [key]: e.target.value }))

  // Ten cyfr, po odrzuceniu myślników i spacji, którymi ludzie NIP-y zapisują.
  // Sprawdzane tylko wtedy, gdy ktoś poprosił o fakturę — przepuszczenie
  // niekompletnego numeru oznacza dokument, którego nie da się wystawić.
  const taxDigits = data.taxId.replace(/\D/g, '')
  const invoiceReady = !invoice || (data.companyName.trim().length > 1 && taxDigits.length === 10)

  const canSubmit =
    !cartEmpty &&
    blocked === null &&
    data.firstName.trim().length > 1 &&
    data.lastName.trim().length > 1 &&
    /\S+@\S+\.\S+/.test(data.email) &&
    invoiceReady &&
    accepted &&
    (!needsMaterialConsent || materialOk) &&
    (!hasRental || rentalOk) &&
    !busy

  // ── WHY THE EVENT IS OPTIONAL ────────────────────────────────────────────
  // The slider below is what places the order, and a gesture has no submit
  // event to hand over. The card stays a `<form>` all the same — `AuthCard`
  // renders one only when `onSubmit` is passed, and a form is what makes the
  // browser treat name, e-mail and phone as one set to autofill.
  //
  // That handler is now a belt-and-braces path rather than the main one. With
  // no submit button in the form, four text fields is more than the one the
  // HTML spec lets implicitly submit, so pressing Enter in a field does
  // nothing — which is the point. `SlideCommit` covers the other direction:
  // if something ever did submit the form from outside, `busy` flips and the
  // pill parks itself at the end instead of claiming the order has not gone.
  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!canSubmit) {
      // Reached when the basket goes invalid UNDER a finger that is already
      // mid-drag: `SlideCommit` drops a live drag on the next pointer move, so
      // a hold-still-and-release still commits. Reopening the pill here costs
      // nothing and is the difference between a checkout that refuses once and
      // one that refuses forever.
      reopenSlider()
      return
    }
    setBusy(true)
    // try/finally, which the previous version lacked: a server action that
    // REJECTED — a dropped connection, a deploy mid-submit — skipped
    // `setBusy(false)` entirely and left the button disabled at "Składam
    // zamówienie…" forever, with an unhandled rejection and no way back except
    // a reload that also loses the typed contact details.
    try {
      // BEFORE the order, and it is the first thing inside the try: everything
      // typed into a line has to be on the server before the server is asked
      // what the lines say. Awaited rather than fired, because the whole point
      // is the ordering — see `flushLines`.
      await flushLines()
      const result = await checkoutAction({
        contact: {
          firstName: data.firstName.trim(),
          lastName: data.lastName.trim(),
          email: data.email.trim(),
          phone: data.phone.trim() || undefined,
          ...(invoice ? { companyName: data.companyName.trim(), taxId: taxDigits } : {}),
        },
        acceptedTerms: accepted,
        acceptedPrivacy: accepted,
        acceptedMaterial: materialOk,
        // Spec §11 · I6. The server refuses the basket without it — a tick
        // that can be skipped by posting the form yourself says nothing, and
        // this one carries a 200% penalty.
        acceptedRentalTerms: rentalOk,
        delivery,
        // Spec §13's tick. The server decides whether this basket may have one
        // at all — from eleven positions it adds one regardless.
        coordinator,
        ...(code.trim() ? { discountCode: code.trim() } : {}),
      })

      if (!result.ok) {
        setConflicts(result.conflicts ?? [])
        toast({
          kind: 'error',
          title: 'Nie udało się złożyć zamówienia',
          description: result.error,
        })
        return
      }
      setConflicts([])
      toast({
        kind: 'success',
        title: 'Zamówienie przyjęte',
        // Only send somebody to their inbox when something was actually posted
        // there. "Sprawdź skrzynkę" against a letter that never left is how a
        // working order starts looking like a broken one.
        description: result.confirmationSent
          ? `Numer ${result.orderNumber}. Sprawdź skrzynkę ${data.email}.`
          : `Numer ${result.orderNumber}. Zapisz go — potwierdzenie mailem nie wyszło.`,
      })
      onPlaced({
        orderNumber: result.orderNumber,
        hasQuoteOnlyItems: result.hasQuoteOnlyItems,
        confirmationSent: result.confirmationSent,
        payment: result.payment,
        payOnlineHref: result.payOnlineHref,
        chatHref: result.chatHref,
      })
    } catch {
      toast({
        kind: 'error',
        title: 'Nie udało się złożyć zamówienia',
        description: 'Spróbuj ponownie za chwilę albo napisz do nas.',
      })
    } finally {
      setBusy(false)
      // THE LINE THE PAYMENT PATH DEPENDS ON. A spent `SlideCommit` ignores
      // every pointer and every key for the rest of its life, so without a new
      // `key` the second attempt after a taken date, a rate limit or a dropped
      // connection would do literally nothing and the person would be looking
      // at a checkout that has quietly stopped being a checkout.
      //
      // In `finally` rather than in the two failure branches, because the set
      // of ways this can fail is not closed — anything that gets here without
      // an order has to leave the pill usable. On the success path the parent
      // swaps the whole grid for `SuccessPanel` in the same batch, so the fresh
      // instance is unmounted before it ever paints — and the effect that puts
      // focus back never runs, because the card it belongs to is gone.
      reopenSlider()
    }
  }

  if (!signedIn) return <OrderSignInCard id={id} locale={locale} className={className} />

  return (
    <AuthCard
      id={id}
      onSubmit={handleSubmit}
      className={cn('scroll-mt-28 flex flex-col gap-5', className)}
    >
      <AuthEyebrow>Dane do zamówienia</AuthEyebrow>

      <div>
        <h2 className="text-[1.5rem] font-bold tracking-tight text-white sm:text-[1.7rem]">
          Twoje dane
        </h2>
        <p className={cn(BODY, 'mt-1 text-white/60')}>
          Masz już konto?{' '}
          <Link
            href={`/${locale}/login`}
            className="font-semibold text-white underline-offset-4 hover:underline"
          >
            Zaloguj się
          </Link>
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <AuthField
          id="firstName"
          label="Imię"
          value={data.firstName}
          onChange={onChange('firstName')}
          autoComplete="given-name"
          placeholder="np. Ola"
        />
        <AuthField
          id="lastName"
          label="Nazwisko"
          value={data.lastName}
          onChange={onChange('lastName')}
          autoComplete="family-name"
          placeholder="np. Nowak"
        />
      </div>

      <AuthField
        id="email"
        type="email"
        label="E-mail"
        value={data.email}
        onChange={onChange('email')}
        autoComplete="email"
        placeholder="ty@example.com"
      />

      <AuthField
        id="phone"
        type="tel"
        label="Telefon (opcjonalnie)"
        value={data.phone}
        onChange={onChange('phone')}
        autoComplete="tel"
        placeholder="600 100 200"
      />

      <label className={cn(BODY, 'flex cursor-pointer items-center gap-3 text-white/70')}>
        <input
          type="checkbox"
          checked={invoice}
          onChange={(e) => setInvoice(e.target.checked)}
          className="h-4 w-4 accent-white"
        />
        Chcę fakturę na firmę
      </label>

      {invoice ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <AuthField
            id="companyName"
            label="Nazwa firmy"
            value={data.companyName}
            onChange={onChange('companyName')}
            autoComplete="organization"
            placeholder="np. Flou sp. z o.o."
          />
          <AuthField
            id="taxId"
            label="NIP"
            value={data.taxId}
            onChange={onChange('taxId')}
            autoComplete="off"
            inputMode="numeric"
            placeholder="1234567890"
          />
        </div>
      ) : null}

      {/* Under the contact and above the consents, where a code is looked for:
          it is the last thing about the MONEY, and everything below it is about
          the agreement. Refused server-side with a sentence (spec §14) — a code
          that silently comes off nothing is the worst of the three possible
          behaviours. */}
      {/* ── WHY 64 AND NOT THE COMPONENT'S DEFAULT TWELVE ──────────────────
          `maxLength` truncates in silence, and this is the payment path.
          `CreateDiscountCodeInput` mints codes up to sixty-four characters and
          the consultation bonus alone is `SPOTKANIE-` plus six — sixteen, past
          the default. A cut code is not a shorter code: it is a code the server has
          never heard of, so somebody who typed theirs correctly is told to check
          their spelling. Sixty-four squares would wrap down the card, but only
          for a code nobody mints — the row only shows what has been typed.

          ── WHY NO `status` ────────────────────────────────────────────────
          Nothing here knows that it was the CODE that was refused.
          `checkoutAction` answers a failed order with one Polish sentence, and
          `codeProblem`'s refusals arrive inside it indistinguishable from
          "wybierz termin". Spilling the row would mean matching on sentences
          written in `@repo/core`; a row that spills for the wrong reason is
          worse than one that never spills at all. */}
      <div className="flex flex-col gap-1.5">
        {/* The example is drawn at 50% white and not the 40% a hint usually
            gets. At 0,75rem on this card it works out at 3,8:1 against the
            glass, under the 4,5:1 that text this size has to clear — and it is
            not decoration, it is the only thing on screen that says what a code
            looks like. The placeholder it replaces was painted at 55% for the
            same reason (`authInputClass`); 50% stays quieter than the label and
            still reads at 5,2:1. */}
        <span className="text-[0.75rem] font-medium text-white/55">
          Kod rabatowy (opcjonalnie) <span className="text-white/50">· np. WIOSNA24</span>
        </span>
        {/* ── WHY THERE IS NO `onSubmit` HERE ANY MORE ──────────────────────
            It used to hand its Enter straight to `handleSubmit`, so that the
            discount row kept the implicit submission that swallowing Enter
            inside the slots had cost it. That was right while a button placed
            the order; it is wrong now. The slider exists so that the order is
            placed by one deliberate gesture, and a hidden shortcut in the
            discount field that skips the gesture is precisely the accidental
            checkout the gesture was put there to prevent. Enter in the code row
            now does what Enter in every other field on this card does. */}
        <CodeSlots
          value={code}
          onChange={setCode}
          // Five and not the default six, because of how little room this card
          // has: the rail is 24rem and the phone is narrower still, and once the
          // panel's own 32px of padding is off, 271px is left at 375. A slot is
          // 44 plus a 6px gap, so six of them need 294 and the sixth drops onto
          // a line of its own — an empty field resting as five squares and a
          // stray. The row still grows past five as soon as anything is typed;
          // it is the RESTING state that has to fit, and three characters is the
          // shortest code `CreateDiscountCodeInput` will mint.
          minSlots={5}
          maxLength={64}
          ariaLabel="Kod rabatowy (opcjonalnie)"
        />
      </div>

      {/* Above the terms, because it is about the ORDER rather than about us:
          spec §7 hangs "brak roszczeń z tytułu opóźnienia" on this tick, so it
          is the one box here whose meaning the customer has to weigh. */}
      {needsMaterialConsent ? (
        <MaterialCheckbox checked={materialOk} onChange={setMaterialOk} />
      ) : null}
      {/* Spec §11 · I6, and only when there is gear to deliver: a basket of
          thumbnails asked how it wants its camera sent is a question about
          nothing. */}
      {hasRental ? (
        <>
          <DeliveryChoice value={delivery} onChange={setDelivery} />
          <RentalTermsCheckbox checked={rentalOk} onChange={setRentalOk} locale={locale} />
        </>
      ) : null}
      {/* Above the consent tick, deliberately: what somebody is agreeing to
          has to be on the screen BEFORE the box that says they agree. */}
      {terms}

      <ConsentCheckbox checked={accepted} onChange={setAccepted} locale={locale} />

      <NextStepsNote />

      {blocked && !cartEmpty ? (
        <p className={cn(BODY, 'text-[oklch(0.8_0.15_60)]')}>{blocked}</p>
      ) : null}

      {/* Named, not counted. "Coś jest zajęte" sends somebody back to a basket
          of five positions to guess which one. */}
      {conflicts.length > 0 ? (
        <div
          role="alert"
          className="rounded-2xl border border-[oklch(0.8_0.15_60_/_0.35)] bg-[oklch(0.8_0.15_60_/_0.1)] px-4 py-3"
        >
          <p className={cn(BODY, 'font-semibold text-[oklch(0.85_0.15_60)]')}>
            Te terminy właśnie zniknęły:
          </p>
          <ul className={cn(BODY, 'mt-1 list-none text-white/70')}>
            {conflicts.map((conflict) => (
              <li key={`${conflict.label}-${conflict.from}`}>
                {conflict.label} — {conflict.from}
                {conflict.to && conflict.to !== conflict.from ? ` … ${conflict.to}` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* ── WHY A SLIDER AND NOT THE BUTTON IT REPLACES ───────────────────
          Owner: „takie coś możemy dać jako przejdź do płatności". This is the
          one control on the site that commits somebody to paying, and until
          now it was a white pill in the exact spot where every other card on
          this page puts something harmless. A drag cannot be produced by a
          stray click, a trackpad bounce or a double submit.

          ── THE WORDS DID NOT CHANGE ───────────────────────────────────────
          Same three sentences the button carried, in the same three states:
          the basket's size decides between „Dodaj pozycję do zamówienia" and
          „Złóż zamówienie", and `busy` is what says „Składam zamówienie…".
          `SlideCommit` puts the resting one in its `aria-label` too, so
          anything that looked this control up by name still finds it.

          ── AND NEITHER DID WHEN IT REFUSES ────────────────────────────────
          `disabled` is `!canSubmit` and nothing else — the same expression,
          unpicked and unmoved. Empty basket, unfinished position, missing
          consent, half-typed NIP, an order already on its way: all of them
          still refuse, and now they refuse a gesture instead of a click. */}
      {/* A plain wrapper, and it earns its place: it is the one node that
          SURVIVES the remount, so it is what `reopenSlider` measures focus
          against and what the effect queries the new pill out of. It changes
          nothing on screen — the pill is `w-full` and the card is a column, so
          one in-flow child is still one in-flow child, and the component's
          `sr-only` status line is `position: absolute` and never was one. */}
      <div ref={commitRef}>
        <SlideCommit
          key={attempt}
          label={cartEmpty ? 'Dodaj pozycję do zamówienia' : 'Złóż zamówienie'}
          committedLabel="Składam zamówienie…"
          onCommit={() => {
            void handleSubmit()
          }}
          disabled={!canSubmit}
          pending={busy}
          // ── MEASURED, NOT GUESSED ───────────────────────────────────────
          // This rail is the narrowest column on the site, and the pill's own
          // `min-w-56` is 224px: at a 375 viewport the card leaves 269px inside
          // its padding, but at 320 it leaves 214, and `AuthCard` is
          // `overflow-hidden`. Ten pixels would be clipped off the RIGHT end —
          // the end the handle has to reach. Travel is measured by the
          // component itself, so a narrower pill is a shorter drag (213px at
          // 375, 158 at 320) and not a threshold nobody can get to.
          //
          // What the width does cost is the label, which is centred between the
          // handle and the right edge: 185px of room at 375, 130 at 320,
          // against 114px for „Złóż zamówienie" — so the one that matters fits
          // on every phone. The empty-basket sentence is longer and truncates,
          // and it is the only one that does at 375; it also names a state
          // where the pill is greyed out and the reader is being pointed at the
          // list instead.
          className="min-w-0"
        />
      </div>
    </AuthCard>
  )
}

/**
 * "Link i materiały są prawidłowe" (spec §7 · G4).
 *
 * Its own control rather than a third meaning bolted onto `ConsentCheckbox`,
 * and the difference is what it does. That box is a legal acceptance of two
 * documents; this one is a factual claim about the customer's own files, made
 * at a moment, with a consequence attached — spec §7 says a bad link is "problem
 * klienta, brak roszczeń z tytułu opóźnienia". The order stores WHEN it was
 * ticked for exactly that reason, so the sentence beside it says what is being
 * claimed rather than asking somebody to agree to something.
 *
 * The server refuses the order without it, so unticking this is not a way to
 * order faster — it is a way not to order at all.
 */
/** The tick itself, shared by every declaration this card asks for. */
function Tick({
  checked,
  onChange,
  children,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  children: ReactNode
}) {
  return (
    <label
      className={cn(
        META,
        'mt-1 flex cursor-pointer items-start gap-3 leading-relaxed text-white/65',
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="peer sr-only"
      />
      <span
        aria-hidden
        className={cn(
          'mt-0.5 grid size-5 shrink-0 place-items-center rounded-md border transition-colors',
          checked ? 'border-white bg-white text-black' : 'border-white/25 bg-white/[0.04]',
        )}
      >
        {checked ? <ArrowRight className="size-3.5 rotate-[-45deg]" /> : null}
      </span>
      <span>{children}</span>
    </label>
  )
}

function MaterialCheckbox({
  checked,
  onChange,
}: {
  checked: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <Tick checked={checked} onChange={onChange}>
      Potwierdzam, że <span className="font-medium text-white/85">link działa</span> i materiały są
      kompletne. Jeśli montażysta nie ma z czego pracować, termin przesuwa się o czas potrzebny na
      dosłanie poprawnego materiału.
    </Tick>
  )
}

/**
 * Spec §11: "Checkbox akceptacji regulaminu najmu wymagany."
 *
 * The one tick on this card that carries a number, so the number is in it: a
 * late return is 200 % of the daily rate per day. Burying that in a linked
 * document and asking for a tick would be asking somebody to agree to a
 * penalty they have not read.
 *
 * The link opens in a NEW TAB and stops its click, for the reason
 * `ConsentCheckbox` writes down and this control had never applied: a link
 * inside a `<label>` toggles the box on its way out, and a same-tab navigation
 * away from a checkout takes a filled basket, a typed address and a phone
 * number with it. Somebody reading the terms they are being asked to accept
 * should not be punished for reading them.
 */
function RentalTermsCheckbox({
  checked,
  onChange,
  locale,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  locale: string
}) {
  return (
    <Tick checked={checked} onChange={onChange}>
      Akceptuję{' '}
      <Link
        href={`/${locale}/regulamin#wynajem-sprzetu`}
        target="_blank"
        rel="noopener"
        onClick={(e) => e.stopPropagation()}
        className="font-medium text-white/85 underline underline-offset-4 hover:text-white"
      >
        regulamin najmu
      </Link>
      : od wydania do zwrotu odpowiadam za sprzęt, a za każdy dzień opóźnienia płacę{' '}
      <span className="font-medium text-white/85">200% stawki dobowej</span>.
    </Tick>
  )
}
